import { useApplicationStore } from '@/stores/application.store';

const maxRetries = 10;
const defaultSiteKey = 'abc';
const isLoaded = ref<boolean>(false);
const loadRetries = ref<number>(0);
const isReady = ref<boolean>(false);
const readyRetries = ref<number>(0);
const isBadgeHidden = ref<boolean>(false);

export default function useRecaptcha() {
    const applicationStore = useApplicationStore();

    const siteKey = computed(() => applicationStore.captchaKey);

    const injectRecaptcha = () => {
        if ('grecaptcha' in window) {
            return;
        }

        const script = document.createElement('script');

        script.type = 'text/javascript';
        script.async = true;
        script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey.value}`;

        script.onload = () => {
            isLoaded.value = true;

            (window as any).grecaptcha.ready(() => {
                isReady.value = true;
            });
        };

        document.body.appendChild(script);
    };

    const load = () => {
        const interval = window.setInterval(() => {
            loadRetries.value++;

            if (siteKey.value) {
                if (siteKey.value === defaultSiteKey) {
                    isLoaded.value = true;
                    isReady.value = true;
                }

                injectRecaptcha();
                clearInterval(interval);
            } else if (loadRetries.value >= maxRetries) {
                clearInterval(interval);
            }
        }, 500);
    };

    const toggleBadge = (show: boolean) => {
        if ((show && !isBadgeHidden.value) || (!show && isBadgeHidden.value)) {
            return;
        }

        const recaptchaContainer = document.getElementsByClassName('grecaptcha-badge');

        if (recaptchaContainer.length) {
            (recaptchaContainer[0] as HTMLElement).style.display = show ? 'block' : 'none';
            isBadgeHidden.value = !show;
        }
    };

    const close = () => {
        toggleBadge(false);
    };

    const execute = (action = 'login'): Promise<string> => {
        return new Promise((resolve, reject) => {
            const interval = window.setInterval(() => {
                readyRetries.value++;

                if (isReady.value) {
                    if (siteKey.value === defaultSiteKey) {
                        clearInterval(interval);
                        resolve(defaultSiteKey);

                        return defaultSiteKey;
                    }

                    (window as any).grecaptcha
                        .execute(siteKey.value, { action })
                        .then((token: string) => {
                            clearInterval(interval);
                            resolve(token);

                            return token;
                        })
                        .catch((error) => {
                            clearInterval(interval);
                            reject(error);
                        });
                } else if (readyRetries.value >= maxRetries) {
                    clearInterval(interval);
                    reject(new Error('Recaptcha timed out'));
                }
            }, 500);
        });
    };

    load();
    toggleBadge(true);

    return { execute, close };
}

export { defaultSiteKey };
